import { readonly, ref, watch } from 'vue'

/** Visual props for {@link LoadingScrimOverlay} (excludes lifecycle-only show options). */
export interface HandwritingSubmitLoadingScrimContent {
    heroImageSrc?: string
    heroAlt?: string
    statusEyebrow?: string
    statusTitlePrefix?: string
    statusTitleEmphasis?: string
    statusTitleSuffix?: string
    recipientEyebrow?: string
    recipientName?: string
    recipientAddress?: string
    returnEyebrow?: string
    returnName?: string
    returnAddress?: string
    zIndex?: number
    ariaLabel?: string
}

/**
 * Options for {@link useHandwritingSubmitLoadingScrim}'s `show`.
 */
export interface HandwritingSubmitLoadingScrimShowOptions
    extends HandwritingSubmitLoadingScrimContent {
    /** When false (default), `hide()` runs automatically on first route change after `show`. */
    persistOnRouteChange?: boolean
    /** Auto-hide safeguard; default from `app.config.ts` → `loadingScrim.defaultTimeoutMs`. */
    timeoutMs?: number
}

const DEFAULT_CONTENT: HandwritingSubmitLoadingScrimContent = {
    heroImageSrc: '/images/processing-order-hero.svg',
    heroAlt: 'Illustration of pen writing on stationery',
    statusEyebrow: 'STATUS',
    statusTitlePrefix: 'Processing ',
    statusTitleEmphasis: 'order',
    statusTitleSuffix: '...',
    recipientEyebrow: 'RECIPIENT',
    recipientName: '',
    recipientAddress: '',
    returnEyebrow: 'RETURN ADDRESS',
    returnName: '',
    returnAddress: '',
    zIndex: 80,
    ariaLabel: 'Processing order',
}

/** Module-scoped singleton state (shared across every `useHandwritingSubmitLoadingScrim()` call). */
const scrimOpen = ref(false)
const scrimContent = ref<HandwritingSubmitLoadingScrimContent>({ ...DEFAULT_CONTENT })

let timeoutId: ReturnType<typeof setTimeout> | null = null
let routeWatcher: ReturnType<typeof watch> | null = null
let persistOnRouteChange = false

/**
 * Singleton Processing scrim for handwritten-note submit and checkout place-order.
 * Renders via {@link HandwritingSubmitLoadingScrimHost} at the app root (`host` bindings).
 */
export function useHandwritingSubmitLoadingScrim() {
    function clearScrimTimers(): void {
        if (timeoutId) {
            clearTimeout(timeoutId)
            timeoutId = null
        }
        if (routeWatcher) {
            routeWatcher()
            routeWatcher = null
        }
    }

    function hide(): void {
        if (!import.meta.client) {
            return
        }

        persistOnRouteChange = false
        scrimOpen.value = false
        clearScrimTimers()
    }

    function applyShowOptions(options: HandwritingSubmitLoadingScrimShowOptions): void {
        const { persistOnRouteChange: persistOpt, timeoutMs: timeoutOpt, ...content } = options

        persistOnRouteChange = persistOpt ?? false
        scrimContent.value = { ...DEFAULT_CONTENT, ...content }
        scrimOpen.value = true

        clearScrimTimers()

        const appConfig = useAppConfig()
        const defaultTimeoutMs = appConfig.loadingScrim?.defaultTimeoutMs ?? 10000
        const timeoutMs = timeoutOpt ?? defaultTimeoutMs
        timeoutId = setTimeout(() => {
            hide()
        }, timeoutMs)

        if (!persistOnRouteChange) {
            const route = useRoute()
            routeWatcher = watch(
                () => route.path,
                () => {
                    hide()
                },
                { once: true }
            )
        }
    }

    const show = async (options: HandwritingSubmitLoadingScrimShowOptions = {}): Promise<void> => {
        if (!import.meta.client) {
            return
        }
        applyShowOptions(options)
    }

    return {
        show,
        hide,
        /** Read-only bindings for {@link HandwritingSubmitLoadingScrimHost}. */
        host: {
            open: readonly(scrimOpen),
            content: readonly(scrimContent),
        },
    }
}
